import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { forkJoin } from 'rxjs';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';

import { PosmProject } from '../../models/posm-project.model';
import { PosmDeliverySchedule } from '../../models/posm-execution.models';
import { PosmService } from '../../posm.service';
import { PageDataPanelComponent } from 'src/app/shared/page-layout/page-data-panel.component';
import { TransportOrderRelease } from 'src/app/transport-planning/transport-planning.model';
import { TransportPlanningService } from 'src/app/transport-planning/transport-planning.service';

interface ReleaseGroupView {
    key: string;
    parent: TransportOrderRelease | null;
    anchor: TransportOrderRelease;
    children: TransportOrderRelease[];
    parentReleaseId?: number;
    kind: 'DIRECT' | 'ORCHESTRATED' | 'ORPHAN_CHILDREN';
}

@Component({
    selector: 'app-posm-project-truck-alignment',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        TableModule,
        TagModule,
        PageDataPanelComponent
    ],
    templateUrl: './posm-project-truck-alignment.component.html',
    styleUrls: ['./posm-project-truck-alignment.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PosmProjectTruckAlignmentComponent implements OnInit {
    public project = input.required<PosmProject>();

    private posmService = inject(PosmService);
    private transportService = inject(TransportPlanningService);
    private router = inject(Router);

    public schedules = signal<PosmDeliverySchedule[]>([]);
    public releases = signal<TransportOrderRelease[]>([]);
    public expandedGroups = signal<Set<string>>(new Set<string>());
    public isLoading = signal(false);
    public message = signal('');

    public readyReleases = computed(() => this.releases().filter(release => this.normalizeStatus(release.validationStatus) === 'READY'));
    public validationErrorReleases = computed(() => this.releases().filter(release => release.validationStatus && release.validationStatus !== 'READY'));
    public shipmentLinkedReleases = computed(() => this.releases().filter(release => !!release.plannedShipmentId));
    public truckAssignedReleases = computed(() => this.releases().filter(release => this.hasTruckAssignment(release)));
    public assignedTruckCount = computed(() => {
        const keys = new Set<string>();
        this.releases().forEach(release => {
            const truck = this.releaseTruckLabel(release);
            const driver = this.releaseDriverLabel(release);
            if (truck === 'Not assigned' && driver === 'Not assigned') return;
            keys.add(`${truck}|${driver}`);
        });
        return keys.size;
    });
    public releaseGroups = computed<ReleaseGroupView[]>(() => {
        const releases = this.sortedReleases(this.releases());
        const parentGroups = new Map<string, ReleaseGroupView>();
        const orphanChildren = new Map<string, TransportOrderRelease[]>();
        const groups: ReleaseGroupView[] = [];

        for (const release of releases) {
            const role = this.releaseRole(release);
            if (role === 'CHILD' && release.parentReleaseId) {
                const key = `parent-${release.parentReleaseId}`;
                const existingParent = parentGroups.get(key);
                if (existingParent) {
                    existingParent.children.push(release);
                } else {
                    const bucket = orphanChildren.get(key) || [];
                    bucket.push(release);
                    orphanChildren.set(key, bucket);
                }
                continue;
            }

            if (role === 'PARENT') {
                const key = `parent-${release.releaseId || release.releaseNumber || groups.length}`;
                const group: ReleaseGroupView = {
                    key,
                    parent: release,
                    anchor: release,
                    children: orphanChildren.get(key) || [],
                    parentReleaseId: release.releaseId,
                    kind: 'ORCHESTRATED'
                };
                parentGroups.set(key, group);
                orphanChildren.delete(key);
                groups.push(group);
                continue;
            }

            groups.push({
                key: `direct-${release.releaseId || release.releaseNumber || groups.length}`,
                parent: release,
                anchor: release,
                children: [],
                parentReleaseId: release.releaseId,
                kind: 'DIRECT'
            });
        }

        for (const [key, children] of orphanChildren.entries()) {
            if (!children.length) continue;
            const anchor = children[0];
            const parentId = anchor.parentReleaseId;
            groups.push({
                key: `orphan-${key}`,
                parent: null,
                anchor,
                children,
                parentReleaseId: parentId,
                kind: 'ORPHAN_CHILDREN'
            });
        }

        return groups;
    });

    ngOnInit(): void {
        this.loadData();
    }

    loadData(): void {
        const projectId = this.project().id;
        const owningCompanyId = this.companyId();
        if (!projectId || !owningCompanyId) return;

        this.isLoading.set(true);
        forkJoin({
            schedules: this.posmService.getDeliverySchedules(projectId, owningCompanyId),
            releases: this.transportService.getPosmProjectReleases(projectId, owningCompanyId)
        }).subscribe({
            next: ({ schedules, releases }) => {
                this.schedules.set(schedules || []);
                this.releases.set(releases || []);
                this.expandedGroups.set(new Set<string>());
                this.message.set('');
                this.isLoading.set(false);
            },
            error: () => {
                this.message.set('Could not load project truck alignment data.');
                this.isLoading.set(false);
            }
        });
    }

    openDeliverySchedule(): void {
        const projectId = this.project().id;
        if (!projectId) return;
        this.router.navigate(['/posm/executions', projectId], {
            queryParams: { tab: 'delivery-schedule' }
        });
    }

    isGroupExpanded(key: string): boolean {
        return this.expandedGroups().has(key);
    }

    toggleGroup(key: string): void {
        this.expandedGroups.update(current => {
            const next = new Set(current);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    }

    releaseLine(release: TransportOrderRelease): string {
        const firstLine = this.primaryLine(release);
        if (!firstLine) return release.sourceNumber || '-';
        return [firstLine.itemNumber || firstLine.itemCode, firstLine.itemName || firstLine.itemDescription]
            .filter(Boolean)
            .join(' - ') || `${(release.lines || []).length} line${(release.lines || []).length === 1 ? '' : 's'}`;
    }

    releaseItemNumber(release: TransportOrderRelease): string {
        const firstLine = this.primaryLine(release);
        return firstLine?.itemNumber || firstLine?.itemCode || release.sourceNumber || '-';
    }

    releaseSkuNumber(release: TransportOrderRelease): string {
        const firstLine = this.primaryLine(release);
        return firstLine?.skuNumber || firstLine?.inventoryStockUnitCode || '-';
    }

    releaseItemDescriptor(release: TransportOrderRelease): string {
        const firstLine = this.primaryLine(release);
        return [
            firstLine?.itemName || firstLine?.itemDescription,
            firstLine?.itemSize
        ].filter(Boolean).join(' | ') || '-';
    }

    releaseTruckLabel(release: TransportOrderRelease): string {
        const linkedSchedule = this.schedules().find(row => row.transportReleaseId === release.releaseId && (row.truckName || row.truck || row.assignedVehicleId));
        return linkedSchedule?.truckName
            || linkedSchedule?.truck
            || release.assignedVehicleLabel
            || (release.assignedVehicleId ? `Vehicle #${release.assignedVehicleId}` : 'Not assigned');
    }

    releaseDriverLabel(release: TransportOrderRelease): string {
        const linkedSchedule = this.schedules().find(row => row.transportReleaseId === release.releaseId && (row.driverName || row.assignedDriverId));
        return linkedSchedule?.driverName
            || release.assignedDriverName
            || (release.assignedDriverId ? `Driver #${release.assignedDriverId}` : 'Not assigned');
    }

    releaseStoreCount(release: TransportOrderRelease): number {
        return this.schedules().filter(row => row.transportReleaseId === release.releaseId).length;
    }

    planningReason(release: TransportOrderRelease): string {
        const linkedRows = this.schedules().filter(row => row.transportReleaseId === release.releaseId);
        const reason = linkedRows.find(row => row.statusReason)?.statusReason;
        return reason || release.validationMessage || '-';
    }

    releaseRole(release: TransportOrderRelease): string {
        return release.releaseRole || (release.parentReleaseId ? 'CHILD' : 'DIRECT');
    }

    roleLabel(release: TransportOrderRelease): string {
        const role = this.releaseRole(release);
        if (role === 'PARENT') return 'Cross-dock parent';
        if (role === 'CHILD') return this.legLabel(release);
        return 'Direct release';
    }

    roleSeverity(release: TransportOrderRelease): string {
        const role = this.releaseRole(release);
        if (role === 'PARENT') return 'warn';
        if (role === 'CHILD') return 'info';
        return 'secondary';
    }

    legLabel(release: TransportOrderRelease): string {
        if (release.legSequence && release.legCount) {
            return `Leg ${release.legSequence} of ${release.legCount}`;
        }
        return 'Cross-dock leg';
    }

    groupTitle(group: ReleaseGroupView): string {
        if (group.kind === 'DIRECT') return 'Direct Order Release';
        if (group.kind === 'ORPHAN_CHILDREN') return 'Orchestration Parent Missing';
        return 'Original Order Release';
    }

    groupRelease(group: ReleaseGroupView): TransportOrderRelease {
        return group.parent || group.anchor;
    }

    groupChildCountLabel(group: ReleaseGroupView): string {
        const count = group.children.length;
        return `${count} orchestration leg${count === 1 ? '' : 's'}`;
    }

    legExecutionSummary(group: ReleaseGroupView): string {
        const total = group.children.length;
        if (!total) {
            return `${this.planningState(group.anchor).replace(/_/g, ' ').toLowerCase()}`;
        }
        const shipmentLinked = group.children.filter(release => !!release.plannedShipmentId).length;
        const truckPlanned = group.children.filter(release => !release.plannedShipmentId && this.hasTruckAssignment(release)).length;
        const blocked = group.children.filter(release => this.planningState(release) === 'BLOCKED').length;
        const awaiting = Math.max(total - shipmentLinked - truckPlanned - blocked, 0);
        const parts = [`${shipmentLinked}/${total} shipment-linked`];
        if (truckPlanned) parts.push(`${truckPlanned} truck planned`);
        if (blocked) parts.push(`${blocked} blocked`);
        if (awaiting) parts.push(`${awaiting} awaiting`);
        return parts.join(' | ');
    }

    orchestrationLabel(release: TransportOrderRelease): string {
        if (!release.crossDockOrchestrationId) return 'No orchestration';
        return release.crossDockOrchestrationNumber || `Orchestration #${release.crossDockOrchestrationId}`;
    }

    collapsedScheduleNumber(release: TransportOrderRelease): string {
        return release.posmDeliveryScheduleNumber
            || this.primarySchedule(release)?.scheduleNumber
            || release.sourceNumber
            || release.sourceId
            || '-';
    }

    collapsedDate(release: TransportOrderRelease): string {
        const row = this.primarySchedule(release);
        return this.date(
            row?.plannedInstallationDateOverride
            || row?.plannedInstallationDate
            || row?.installationDate
            || row?.jobDate
            || release.pickupWindowStart
            || release.pickupWindowEnd
            || release.deliveryWindowStart
            || release.deliveryWindowEnd
        );
    }

    planningState(release: TransportOrderRelease): string {
        if (release.plannedShipmentId) return 'SHIPMENT_LINKED';
        if (this.hasTruckAssignment(release)) return 'TRUCK_PLANNED';
        if (this.normalizeStatus(release.validationStatus) && this.normalizeStatus(release.validationStatus) !== 'READY') return 'BLOCKED';
        return 'AWAITING_PLANNING';
    }

    planningSeverity(release: TransportOrderRelease): string {
        const state = this.planningState(release);
        if (state === 'SHIPMENT_LINKED') return 'success';
        if (state === 'TRUCK_PLANNED') return 'info';
        if (state === 'BLOCKED') return 'danger';
        return 'warn';
    }

    planningNote(release: TransportOrderRelease): string {
        return [
            release.routeTemplateCode,
            release.assignmentRuleCode,
            release.assignmentMode || release.vehicleTypeName
        ].filter(Boolean).join(' | ') || 'No route template or assignment rule';
    }

    assignmentSummary(release: TransportOrderRelease): string {
        return [
            release.assignmentMode || 'OWN_TRUCK',
            release.vehicleTypeName,
            release.helperCount != null ? `${release.helperCount} helper${release.helperCount === 1 ? '' : 's'}` : null,
            release.manpowerCount != null ? `${release.manpowerCount} crew` : null
        ].filter(Boolean).join(' | ');
    }

    linkedShipmentLabel(release: TransportOrderRelease): string {
        if (!release.plannedShipmentId) return 'No shipment linked';
        return release.shipmentNumber || 'Linked shipment';
    }

    releaseDeliveryLabel(release: TransportOrderRelease): string {
        return release.deliveryNumber ? `Delivery ${release.deliveryNumber}` : this.planningNote(release);
    }

    pickupStoreLabel(release: TransportOrderRelease): string {
        return release.originName || this.primarySchedule(release)?.sourceWarehouseName || '-';
    }

    pickupStoreDetail(release: TransportOrderRelease): string {
        const row = this.primarySchedule(release);
        return row?.sourceWarehouseName
            || (release.originLocationId ? `Location ${release.originLocationId}` : null)
            || 'Pickup pending';
    }

    dropoffStoreLabel(release: TransportOrderRelease): string {
        const row = this.primarySchedule(release);
        return release.destinationName || row?.location?.name || row?.storeName || row?.storeNumber || '-';
    }

    dropoffStoreDetail(release: TransportOrderRelease): string {
        const row = this.primarySchedule(release);
        return [
            row?.storeNumber || row?.location?.externalLocationReference,
            row?.city
        ].filter(Boolean).join(' | ') || `${this.releaseStoreCount(release)} linked task${this.releaseStoreCount(release) === 1 ? '' : 's'}`;
    }

    linkedStoreNames(release: TransportOrderRelease): string {
        const stores = this.schedules()
            .filter(row => row.transportReleaseId === release.releaseId)
            .map(row => row.location?.name || row.storeName || row.storeNumber || null)
            .filter((value): value is string => !!value);
        return Array.from(new Set(stores)).join(', ') || 'Stores pending';
    }

    statusSeverity(status?: string): string {
        if (!status) return 'secondary';
        const normalized = status.toUpperCase();
        if (normalized.includes('READY') || normalized.includes('PLANNED')) return 'info';
        if (normalized.includes('RELEASED') || normalized.includes('DELIVERED') || normalized.includes('COMPLETED')) return 'success';
        if (normalized.includes('ERROR') || normalized.includes('REJECT') || normalized.includes('CANCEL')) return 'danger';
        if (normalized.includes('PENDING') || normalized.includes('DRAFT')) return 'warn';
        return 'secondary';
    }

    date(value?: string | Date | null): string {
        return value ? formatDate(value, 'dd MMM yyyy HH:mm', 'en-US') : '-';
    }

    private hasTruckAssignment(release: TransportOrderRelease): boolean {
        if (release.assignedVehicleId || release.assignedDriverId || release.assignedVehicleLabel || release.assignedDriverName) return true;
        return this.schedules().some(row =>
            row.transportReleaseId === release.releaseId
            && !!(row.truckName || row.truck || row.assignedVehicleId || row.driverName || row.assignedDriverId)
        );
    }

    private primaryLine(release: TransportOrderRelease) {
        return (release.lines || [])[0];
    }

    private primarySchedule(release: TransportOrderRelease): PosmDeliverySchedule | undefined {
        return this.schedules().find(row => row.transportReleaseId === release.releaseId);
    }

    private sortedReleases(releases: TransportOrderRelease[]): TransportOrderRelease[] {
        return [...releases].sort((left, right) => {
            const leftRole = this.releaseRole(left);
            const rightRole = this.releaseRole(right);
            const leftParentKey = left.parentReleaseId || left.releaseId || 0;
            const rightParentKey = right.parentReleaseId || right.releaseId || 0;
            if (leftParentKey !== rightParentKey) return leftParentKey - rightParentKey;
            if (leftRole !== rightRole) {
                if (leftRole === 'PARENT') return -1;
                if (rightRole === 'PARENT') return 1;
            }
            const leftLeg = left.legSequence || 0;
            const rightLeg = right.legSequence || 0;
            if (leftLeg !== rightLeg) return leftLeg - rightLeg;
            const shipmentCompare = Number(!!right.plannedShipmentId) - Number(!!left.plannedShipmentId);
            if (shipmentCompare !== 0) return shipmentCompare;
            const truckCompare = Number(this.hasTruckAssignment(right)) - Number(this.hasTruckAssignment(left));
            if (truckCompare !== 0) return truckCompare;
            return `${left.releaseNumber || ''}`.localeCompare(`${right.releaseNumber || ''}`);
        });
    }

    private normalizeStatus(value?: string | null): string {
        return `${value || ''}`.trim().toUpperCase();
    }

    private companyId(): number {
        return this.project().owningCompanyId || 0;
    }
}
